import React, { useState, useRef } from 'react'
import useTranslation from '~/components/shared/TranslationProvider'
import { Button } from '~/components/_atoms'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/lib/ui/dialog'
import { Textarea } from '~/lib/ui/textarea'
import QuestionWithChoices from './QuestionWithChoices'
import { apiPost } from '~/util/api'

const FeedbackModal = ({
  programLevel,
  isInlineLink,
  btnText,
  isMobileMenu,
  isButton,
  platformName,
}) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [ratings, setRatings] = useState([null, null, null])
  const [submitted, setSubmitted] = useState(false)
  const [additionalFeedback, setAdditionalFeedback] = useState('')
  const [noTeamInteraction, setNoTeamInteraction] = useState(false)
  const [thankYouMessageVisible, setThankYouMessageVisible] = useState(false)
  const webpage =
    window.location.pathname === '/' ? 'Dashboard' : window.location.pathname

  const toggleOpen = () => setOpen(!open)

  const handleRatingChange = (index, rating) => {
    const updatedRatings = [...ratings]
    updatedRatings[index] = rating
    setRatings(updatedRatings)
  }

  const handleSubmit = async (partial = false) => {
    try {
      await apiPost('/rate_us/op_next_submit_feedback', {
        ratings,
        additional_feedback: partial ? null : additionalFeedback,
        web_page: webpage,
      })
      setThankYouMessageVisible(true)

      setTimeout(() => {
        setThankYouMessageVisible(false)
        resetState()
      }, 2000)
    } catch (error) {
      console.error('Failed to submit feedback:', error)
    }
  }

  const resetState = () => {
    setOpen(false)
    setRatings([null, null, null])
    setSubmitted(false)
    setAdditionalFeedback('')
    setNoTeamInteraction(false)
  }

  const handleNoTeamInteractionChange = (checked) => {
    setNoTeamInteraction(checked)
    if (checked) {
      const updatedRatings = [...ratings]
      updatedRatings[2] = null
      setRatings(updatedRatings)
    }
  }

  const allRatingsAboveThreshold = ratings.every(
    (rating) => rating === null || rating >= 3,
  )
  const anyRatingBelowThreshold = ratings.some(
    (rating) => rating !== null && rating <= 2,
  )

  const inlineLinkClasses =
    'inline underline text-[#08467c] hover:text-[#0f87f0]'

  const navLinkClasses =
    'text-normal flex cursor-pointer items-center px-3 py-2.5 font-medium leading-5 tracking-tight text-cod-gray-800 hover:bg-cod-gray-100 rounded-lg hover:border-none hover:text-cod-gray-800 focus:outline-none focus:bg-cod-gray-200 focus:text-cod-gray-800 mx-4'

  const btnPrimaryClasses = 'btn btn-primary !text-lg'

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={toggleOpen}
        className="z-100 top-[72px] w-[90%] rounded-2xl"
      >
        <DialogTrigger asChild className="cursor-pointer">
          {isMobileMenu ? (
            <button className={isButton ? btnPrimaryClasses : navLinkClasses}>
              {btnText || t('title')}
            </button>
          ) : (
            <button
              className={
                isInlineLink
                  ? inlineLinkClasses
                  : isButton
                    ? btnPrimaryClasses
                    : 'inline-flex h-auto cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-none border-b-2 border-transparent !px-0 !py-0 text-base font-normal text-cod-gray-800 underline-offset-4 ring-offset-background transition-colors hover:border-cod-gray-900 hover:border-current hover:text-cod-gray-900 focus:border-cod-gray-900 focus:border-current focus:text-cod-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 active:border-transparent active:text-cod-gray-800 disabled:pointer-events-none disabled:opacity-50 [&.selected]:border-current [&.selected]:font-medium'
              }
            >
              {btnText || t('title')}
            </button>
          )}
        </DialogTrigger>
        {!thankYouMessageVisible && (
          <DialogContent
            className="fix-legacy-hack font-roboto lg:max-w-3xl xl:max-w-5xl"
            variant="default"
            dialogCloseClass="text-black"
          >
            <DialogHeader className="!bg-primary">
              <DialogTitle className="text-left" asChild>
                <div className="text-2xl">{t('title')}</div>
              </DialogTitle>
            </DialogHeader>
            <div className="px-6 pb-4">
              {!submitted ? (
                <FeedbackForm
                  t={t}
                  ratings={ratings}
                  onRatingChange={handleRatingChange}
                  onSubmit={() => setSubmitted(true)}
                  onClose={toggleOpen}
                  programLevel={programLevel}
                  platformName={platformName}
                  noTeamInteraction={noTeamInteraction}
                  onNoTeamInteractionChange={handleNoTeamInteractionChange}
                />
              ) : (
                <FeedbackSummary
                  t={t}
                  allRatingsAboveThreshold={allRatingsAboveThreshold}
                  anyRatingBelowThreshold={anyRatingBelowThreshold}
                  additionalFeedback={additionalFeedback}
                  onFeedbackChange={(e) =>
                    setAdditionalFeedback(e.target.value)
                  }
                  onSubmit={() => handleSubmit(false)}
                  onPartialSubmit={() => handleSubmit(true)}
                />
              )}
            </div>
          </DialogContent>
        )}
        {thankYouMessageVisible && (
          <DialogContent className="text-center">
            <DialogHeader className="min-h-14 !bg-primary" />
            <DialogTitle asChild>
              <div className="visuallyhidden">{t('thank_you')}</div>
            </DialogTitle>
            <div className="flex min-h-24 items-center justify-center">
              <p className="!mb-0 pb-4 text-lg">{t('thanks_for_sharing')}</p>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  )
}

// CTS-9243: explicit Tab chain (Q1→Q2→Q3→Maybe Later→Share Feedback) and component="button" on action buttons for keyboard nav
const FeedbackForm = ({
  t,
  onRatingChange,
  onSubmit,
  onClose,
  programLevel,
  platformName,
  noTeamInteraction,
  onNoTeamInteractionChange,
}) => {
  const q2Ref = useRef(null)
  const q3Ref = useRef(null)
  const maybeLaterRef = useRef(null)

  return (
    <>
      <p>{t('want_your_feedback')}</p>
      <QuestionWithChoices
        question={t('q1', { platformName: platformName })}
        choices={t('q1_choices', { returnObjects: true })}
        onRatingChange={(rating) => onRatingChange(0, rating)}
        onTabOut={() => q2Ref.current?.focusFirst()}
      />
      <QuestionWithChoices
        ref={q2Ref}
        question={t('q2', { platformName: platformName })}
        choices={t('q2_choices', { returnObjects: true })}
        onRatingChange={(rating) => onRatingChange(1, rating)}
        onTabOut={() => {
          if (programLevel !== 'BRONZE' && q3Ref.current) {
            q3Ref.current.focusFirst()
          } else {
            maybeLaterRef.current?.focus()
          }
        }}
      />
      {programLevel !== 'BRONZE' && (
        <>
          <QuestionWithChoices
            ref={q3Ref}
            question={t('q3', { platformName: platformName })}
            choices={t('q3_choices', { returnObjects: true })}
            onRatingChange={(rating) => onRatingChange(2, rating)}
            onTabOut={() => maybeLaterRef.current?.focus()}
            disabled={noTeamInteraction}
            className="mb-3"
          />
          <div className="mb-10">
            <label className="-mt-6 flex cursor-pointer items-center">
              <input
                type="checkbox"
                checked={noTeamInteraction}
                onChange={(e) => onNoTeamInteractionChange(e.target.checked)}
                className="!mt-0 h-6 w-6 rounded border-gray-300"
              />
              <span className="ml-3 text-gray-700">
                {t('no_team_interaction')}
              </span>
            </label>
          </div>
        </>
      )}
      <p>{t('thank_you')}</p>
      <div className="flex flex-row justify-end gap-x-4">
        <Button
          ref={maybeLaterRef}
          component="button"
          variant="solid"
          color="secondary"
          size="sm"
          className="order-last px-6"
          onClick={onClose}
        >
          {t('maybe_later')}
        </Button>
        <Button
          component="button"
          onClick={onSubmit}
          size="sm"
          className="order-first px-6"
        >
          {t('share_feedback', { platformName: platformName })}
        </Button>
      </div>
    </>
  )
}

const FeedbackSummary = ({
  t,
  allRatingsAboveThreshold,
  anyRatingBelowThreshold,
  additionalFeedback,
  onFeedbackChange,
  onSubmit,
  onPartialSubmit,
}) => (
  <>
    {allRatingsAboveThreshold && (
      <>
        <p className="mb-4">{t('thats_fantastic')}</p>
        <p className="mb-4">{t('share_any_additional_info')}</p>
      </>
    )}
    {anyRatingBelowThreshold && <p className="mb-4">{t('learn_more')}</p>}
    <Textarea
      className="mb-4 min-h-36"
      value={additionalFeedback}
      onChange={onFeedbackChange}
    />
    <div className="flex flex-row justify-end gap-x-4">
      <Button
        component="button"
        variant="solid"
        color="secondary"
        size="sm"
        className="order-last px-6"
        onClick={onPartialSubmit}
      >
        {t('maybe_later')}
      </Button>
      <Button
        component="button"
        onClick={onSubmit}
        size="sm"
        className="order-first px-6"
      >
        {t('share_feedback')}
      </Button>
    </div>
  </>
)

export default FeedbackModal
